import React from "react";
import { Form, theme } from "antd";
import { useTranslate } from "@refinedev/core";
import { PlusIcon } from "@phosphor-icons/react";
import { Grid, GridItem } from "@libertify/ui";
import { DashedAddButton } from "../../DashedAddButton";
import { PlaybookLock } from "../../PlaybookLock";
import { NEW_LINK_TYPE } from "../defaults";
import { ExperienceLinkItem } from "./ExperienceLinkItem";
import {
  SectionHeading,
  SectionSubtitle,
} from "./GeneralSettingsConfigCard.styles";

const KEY = "createExperience.generalSettings";

interface HeaderLinksSectionProps {
  /** Guard each row's remove button behind a Popconfirm — on for the edit
   *  card, where removal patches right away. Off (default) for the create
   *  card, where nothing persists until the draft submits. */
  confirmRemove?: boolean;
  /**
   * Fired when a row's label/value input blurs, with that row's index —
   * the edit card uses this to commit text edits without a Save button. No-op
   * if omitted.
   */
  onFieldBlur?: (rowIndex: number) => void;
  /** Ids of links actually saved on the backend — lets a row still confirm
   *  removal even if its fields were cleared in the UI without saving. */
  savedLinkIds?: Set<string>;
}

/**
 * "Header links" (the CTAs shown in the experience header) — shared between
 * the create-flow config card and the edit card, which pre-populates
 * whatever links already exist for a live experience. Must be rendered
 * inside a Form bound to `GeneralSettingsFormValues` (or a superset of it).
 */
export const HeaderLinksSection: React.FC<HeaderLinksSectionProps> = ({
  confirmRemove,
  onFieldBlur,
  savedLinkIds,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();

  return (
    <PlaybookLock variant="fields">
      <div>
        <SectionHeading>{t(`${KEY}.links.heading`)}</SectionHeading>
        <SectionSubtitle $token={token}>
          {t(`${KEY}.links.subtitle`)}
        </SectionSubtitle>
        <Form.List name="links">
          {(fields, { add, remove }) => (
            <>
              <Grid columns={1} gap="md">
                {fields.map((field) => (
                  <GridItem key={field.key}>
                    <ExperienceLinkItem
                      name={field.name}
                      onRemove={() => remove(field.name)}
                      confirmRemove={confirmRemove}
                      savedLinkIds={savedLinkIds}
                      onFieldBlur={
                        onFieldBlur ? () => onFieldBlur(field.name) : undefined
                      }
                    />
                  </GridItem>
                ))}
              </Grid>
              <div style={{ marginTop: 12 }}>
                <DashedAddButton
                  icon={<PlusIcon size={16} weight="bold" />}
                  label={t(`${KEY}.links.add`)}
                  onClick={() =>
                    // A stable id up front, so it isn't re-minted on every save.
                    add({
                      id: crypto.randomUUID(),
                      type: NEW_LINK_TYPE,
                      label: "",
                      value: "",
                    })
                  }
                  iconBordered={false}
                  centered
                />
              </div>
            </>
          )}
        </Form.List>
      </div>
    </PlaybookLock>
  );
};

export default HeaderLinksSection;
